import React from 'react';
import { Share2, Globe, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';
import BrandLogo from '../ui/BrandLogo';
import { showAppAlert } from '../../utils/appAlert';

const Footer = ({ compact = false }) => {
  const handleShareSite = async () => {
    const shareUrl = window.location.origin;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Go Rides',
          text: 'Explore Go Rides premium car rental experiences in Rwanda.',
          url: shareUrl
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        showAppAlert({
          type: 'success',
          title: 'Link copied',
          message: 'Go Rides website link has been copied to your clipboard.'
        });
        return;
      }

      showAppAlert({
        type: 'info',
        title: 'Share link',
        message: shareUrl
      });
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      showAppAlert({
        type: 'error',
        title: 'Share failed',
        message: 'We could not share the Go Rides website right now.'
      });
    }
  };

  return (
    <footer className={`bg-white border-t border-gray-100 ${compact ? 'pt-8 pb-5' : 'pt-20 pb-10'}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className={`grid grid-cols-2 md:grid-cols-4 ${compact ? 'gap-6 mb-6' : 'gap-12 mb-16'}`}>

          {/* Brand Column */}
          <div className="md:col-span-1">
            <div className="mb-6">
              <BrandLogo size="md" />
            </div>
            <p className="text-[9px] text-gray-400 uppercase tracking-widest mb-6 block">Your Journey, Your way</p>
            <p className="text-sm text-gray-500 leading-relaxed pr-4">
              Redefining the car rental experience with elegance, ease, and premium service since 2012.
            </p>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-bold text-gray-900 mb-6 text-sm tracking-wider uppercase">Company</h3>
            <ul className={compact ? 'space-y-2' : 'space-y-4'}>
              <li><Link to="/about" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">About Us</Link></li>
              <li><Link to="/careers" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Careers</Link></li>
              <li><Link to="/browse" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Fleet</Link></li>
              <li><Link to="/car-rental-kigali" className="text-sm text-gray-500 hover:text-gray-900">Car rental in Kigali</Link></li>
              <li><Link to="/suv-rental-rwanda" className="text-sm text-gray-500 hover:text-gray-900">SUV rental in Rwanda</Link></li>
              <li><Link to="/car-rental-with-driver" className="text-sm text-gray-500 hover:text-gray-900">Cars &amp; drivers</Link></li>
              <li><Link to="/services" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Services</Link></li>
            </ul>
          </div>

          {/* Support Column */}
          <div>
            <h3 className="font-bold text-gray-900 mb-6 text-sm tracking-wider uppercase">Support</h3>
            <ul className={compact ? 'space-y-2' : 'space-y-4'}>
              <li><Link to="/contact" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Contact</Link></li>
              <li><Link to="/faqs" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">FAQs</Link></li>
              <li><Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Terms of Service</Link></li>
            </ul>
          </div>

          {/* Social Column */}
          <div>
            <h3 className="font-bold text-gray-900 mb-6 text-sm tracking-wider uppercase">Social</h3>
            <div className="flex gap-4">
              <button type="button" onClick={handleShareSite} aria-label="Share Go Rides" className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors">
                <Share2 className="w-4 h-4" />
              </button>
              <Link to="/" aria-label="Go Rides website" className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors">
                <Globe className="w-4 h-4" />
              </Link>
              <Link to="/browse" aria-label="Go Rides fleet gallery" className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors">
                <Camera className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-100 pt-8 text-center">
          <p className="text-sm text-gray-400">
            &copy; {new Date().getFullYear()} Go Rides Car Rentals. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
